import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, ChevronRight, RotateCcw, ChevronDown, X, Plus, Eye, Sparkles, FlaskConical, ExternalLink, Ban, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DuplicateReviewDialog } from '../components/DuplicateReviewDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { DealFilterPanel, buildExploreBody, DEFAULT_FILTERS, useFilterOptions } from '../components/DealFilterPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PassReasonPicker, passReasonText } from '../components/PassReasonPicker';
import { AiDealSearch } from '../components/AiDealSearch';
import { CodeNameTag } from '../components/CodeNameTag';
import { fmtRevenue1, fmtInt } from '../lib/fieldInfo';
import { useAuth } from '../contexts/AuthContext';

const SORT_LABELS = {
  name: 'Name', sector: 'Sector', revenue: 'Revenue', employees: 'Employees',
  founded: 'Founded', succession_score: 'Succession', infra: 'Infra Fit', rev_per_employee: 'Rev/Employee',
  thesis_score: 'Thesis-Fit',
};
const SORT_DEFAULT_DIR = { name: 'asc', sector: 'asc', infra: 'asc' };
const defaultDirFor = (field) => SORT_DEFAULT_DIR[field] || 'desc';

const STORAGE_KEY = 'explorer-state-v3';

export default function ExplorerPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [dupOpen, setDupOpen] = useState(false);

  // Restore previously searched state (when coming back from a deal)
  const saved = (() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
  })();

  const [filters, setFilters] = useState(() => {
    const f = saved?.filters || DEFAULT_FILTERS;
    if (!Array.isArray(f.sort)) return { ...f, sort: [{ field: f.sort_by || 'name', dir: f.sort_dir || 'asc' }] };
    return f;
  });
  const [results, setResults] = useState(saved?.results || []);
  const [total, setTotal] = useState(saved?.total || 0);
  const [page, setPage] = useState(saved?.page || 1);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const { sectorConfig, ownerOptions } = useFilterOptions();
  const [viewedSet, setViewedSet] = useState(() => new Set());
  const [addSortOpen, setAddSortOpen] = useState(false);
  const [sortAddField, setSortAddField] = useState('revenue');
  const [sortAddDir, setSortAddDir] = useState('desc');
  const restoredRef = useRef(!!saved);
  const didMountRef = useRef(false);
  const [heurStatus, setHeurStatus] = useState(null);
  const heurPollRef = useRef(null);
  const [quickScoring, setQuickScoring] = useState(null);
  const [bulkRun, setBulkRun] = useState(null);
  const bulkPollRef = useRef(null);
  const [expandedDesc, setExpandedDesc] = useState(() => new Set());
  const [passTarget, setPassTarget] = useState(null);
  const [passText, setPassText] = useState('');
  const [passTags, setPassTags] = useState([]);
  const [passBench, setPassBench] = useState(false);
  const [passing, setPassing] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [aiRunId, setAiRunId] = useState(saved?.aiRunId || null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const onAiResults = useCallback((res, meta) => {
    if (res === null) {
      // AI search cleared → back to the normal explorer view
      setAiResults(null); setAiStatus(null); setAiRunId(null);
      searchRef.current?.(1, null);
      return;
    }
    setAiStatus(meta?.status || null);
    if (meta?.status === 'done' && meta?.runId) {
      // finished → switch to server-side intersection (all filters apply)
      setAiResults(null);
      setAiRunId(meta.runId);
      searchRef.current?.(1, meta.runId);
    } else {
      setAiResults(res);
    }
  }, []);

  const getAiFilters = useCallback(() => buildExploreBody({ ...filtersRef.current }), []);

  const pollHeuristics = useCallback(() => {
    clearInterval(heurPollRef.current);
    heurPollRef.current = setInterval(async () => {
      try {
        const r = await api.heuristicsStatus();
        setHeurStatus(r.data);
        if (r.data.run?.status !== 'running') {
          clearInterval(heurPollRef.current);
          if (r.data.run?.status === 'done') {
            toast.success(`Heuristik fertig — ${(r.data.run.updated_succession || 0).toLocaleString()} Succession-Scores, ${(r.data.run.updated_infra || 0).toLocaleString()} Infra-Fits ergänzt`);
            searchRef.current?.(1);
          } else if (r.data.run?.status === 'failed') {
            toast.error('Heuristik-Lauf fehlgeschlagen');
          } else if (r.data.run?.status === 'interrupted') {
            toast.warning('Heuristik-Lauf wurde durch einen Server-Neustart unterbrochen — bitte erneut starten');
          }
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, []);

  const startHeuristics = async () => {
    try {
      const r = await api.runHeuristics();
      setHeurStatus({ run: r.data });
      toast.success(`Heuristik gestartet — ${(r.data.total || 0).toLocaleString()} Firmen werden geprüft`);
      pollHeuristics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };

  const pollBulk = useCallback(() => {
    clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      try {
        const r = await api.bulkQuickScoreStatus();
        setBulkRun(r.data.run);
        if (r.data.run?.status !== 'running') {
          clearInterval(bulkPollRef.current);
          if (r.data.run?.status === 'done') {
            toast.success(`KI-Nachschärfung fertig — ${r.data.run.updated || 0} Deals neu bewertet${r.data.run.failed ? `, ${r.data.run.failed} fehlgeschlagen` : ''}`);
            searchRef.current?.(1);
          } else if (r.data.run?.status === 'interrupted') {
            toast.warning('KI-Nachschärfung wurde durch einen Server-Neustart unterbrochen — bitte erneut starten');
          } else if (r.data.run?.status === 'failed') {
            toast.error('KI-Nachschärfung fehlgeschlagen');
          }
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, []);

  const startBulk = async () => {
    const f = { ...filtersRef.current };
    try {
      const r = await api.bulkQuickScore({
        ...buildExploreBody(f),
        ai_run_id: aiRunId || undefined,
        sort: Array.isArray(f.sort) && f.sort.length ? f.sort : [{ field: 'name', dir: 'asc' }],
      });
      setBulkRun(r.data);
      toast.success(`KI-Nachschärfung gestartet — ${r.data.total} Deals${r.data.skipped ? ` (${r.data.skipped} bereits KI-bewertet übersprungen)` : ''}`);
      pollBulk();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };

  useEffect(() => {
    api.getViewedDeals()
      .then(res => setViewedSet(new Set(res.data?.project_ids || [])))
      .catch(() => {});
    api.heuristicsStatus().then(r => {
      setHeurStatus(r.data);
      if (r.data.run?.status === 'running') pollHeuristics();
    }).catch(() => {});
    api.bulkQuickScoreStatus().then(r => {
      if (r.data.run?.status === 'running') { setBulkRun(r.data.run); pollBulk(); }
    }).catch(() => {});
    return () => { clearInterval(heurPollRef.current); clearInterval(bulkPollRef.current); };
  }, []);

  const persist = useCallback((f, r, t, p, ai) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ filters: f, results: r, total: t, page: p, aiRunId: ai || null })); } catch { /* ignore quota */ }
  }, []);

  const search = useCallback(async (p = 1, aiOverride) => {
    setLoading(true);
    const useAi = aiOverride !== undefined ? aiOverride : aiRunId;
    try {
      const f = { ...filters };
      const res = await api.exploreProjects({
        ...buildExploreBody(f),
        ai_run_id: useAi || undefined,
        sort: Array.isArray(f.sort) && f.sort.length ? f.sort : [{ field: 'name', dir: 'asc' }],
        page: p,
        per_page: 50,
      });
      const next = p === 1 ? res.data.results : [...results, ...res.data.results];
      setResults(next);
      setTotal(res.data.total);
      setPage(p);
      persist(f, next, res.data.total, p, useAi);
    } catch { toast.error('Search failed'); }
    setLoading(false);
  }, [filters, results, persist, aiRunId]);

  // Initial load — skip if we restored previously searched results or an AI run
  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    let aiSaved = null;
    try { aiSaved = JSON.parse(sessionStorage.getItem('explorer-ai-search-v1') || 'null'); } catch { /* ignore */ }
    if (aiSaved?.runId) return; // the AI restore triggers the search with ai_run_id itself
    search(1);
    // eslint-disable-next-line
  }, []);

  // Auto-search on filter change (debounced); skip the mount-time run
  const sortKey = JSON.stringify(filters.sort || []);
  const kwKey = JSON.stringify([filters.include_keywords || [], filters.exclude_keywords || [], filters.include_mode || 'any']);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    const timer = setTimeout(() => search(1), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [filters.sector, filters.recurring_revenue, filters.succession_case, filters.infrastructure_adjacent, filters.rating_source, filters.deal_type, filters.pipeline_stage, filters.owner_id, filters.subsector, sortKey, kwKey, filters.exclude_claimed, filters.ac_flagged, filters.ac_feedback,
     filters.founded_min, filters.founded_max, filters.employees_min, filters.employees_max,
     filters.revenue_min, filters.revenue_max, filters.owner_age_min, filters.owner_age_max,
     filters.rpe_min, filters.rpe_max,
     filters.succession_score_min, filters.succession_score_max,
     filters.exclude_viewed, filters.exclude_pass_proposed, filters.asset_heavy_min, filters.asset_heavy_max, filters.import_category]);

  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const searchRef = useRef();
  searchRef.current = search;

  const quickScore = async (e, r) => {
    e.stopPropagation();
    setQuickScoring(r.id);
    try {
      const res = await api.aiQuickScore(r.id);
      setResults(prev => prev.map(x => (x.id === r.id ? { ...x, ...res.data } : x)));
      setAiResults(prev => prev ? prev.map(x => (x.id === r.id ? { ...x, ...res.data } : x)) : prev);
      toast.success(`KI-Analyse ${r.name}: Succession ${res.data.succession_score}/100 · Infra ${res.data.infra_adjacent}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'KI-Analyse fehlgeschlagen'); }
    setQuickScoring(null);
  };
  const resetFilters = () => { setFilters(DEFAULT_FILTERS); try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ } };

  const toggleDesc = (e, id) => {
    e.stopPropagation();
    setExpandedDesc(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openPassDialog = (e, r) => {
    e.stopPropagation();
    setPassTarget(r); setPassText(''); setPassTags([]); setPassBench(false);
  };

  const submitPass = async () => {
    const reason = [passReasonText(passTags), passText.trim()].filter(Boolean).join('; ');
    if (!reason) { toast.error('Bitte Grund wählen oder eintippen'); return; }
    setPassing(true);
    try {
      const res = await api.turnDownProject(passTarget.id, reason, passBench, 'explorer', passTags);
      if (res.data.status === 'passed') {
        const patch = (x) => (x.id === passTarget.id
          ? { ...x, pipeline_stage: 'passed', stage_label: 'Passed', benched: passBench } : x);
        setResults(prev => prev.map(patch));
        setAiResults(prev => prev ? prev.map(patch) : prev);
        toast.success(`${passTarget.name} passed`);
      } else {
        toast.success(res.data.message || 'Pass vorgeschlagen — wartet auf Bestätigung');
      }
      setPassTarget(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Pass fehlgeschlagen'); }
    setPassing(false);
  };

  const websiteUrl = (w) => (w.startsWith('http') ? w : `https://${w}`);

  const openDeal = (id) => { setViewedSet(prev => new Set(prev).add(id)); persist(filters, results, total, page); navigate(`/projects/${id}`, { state: { from: '/explorer' } }); };

  // Multi-level sort. Normal click = single sort (toggle dir if already sole). Shift+Click = add/toggle a level.
  const toggleSort = (field, additive) => {
    setFilters(prev => {
      const chain = Array.isArray(prev.sort) ? [...prev.sort] : [];
      const idx = chain.findIndex(s => s.field === field);
      if (additive) {
        if (idx >= 0) {
          chain[idx] = { ...chain[idx], dir: chain[idx].dir === 'asc' ? 'desc' : 'asc' };
        } else {
          chain.push({ field, dir: defaultDirFor(field) });
        }
        return { ...prev, sort: chain };
      }
      if (chain.length === 1 && chain[0].field === field) {
        return { ...prev, sort: [{ field, dir: chain[0].dir === 'asc' ? 'desc' : 'asc' }] };
      }
      return { ...prev, sort: [{ field, dir: defaultDirFor(field) }] };
    });
  };

  const removeSort = (field) => setFilters(prev => {
    const chain = (prev.sort || []).filter(s => s.field !== field);
    return { ...prev, sort: chain.length ? chain : [{ field: 'name', dir: 'asc' }] };
  });

  // Explicit add-sort (dropdown UI). Appends a level, or updates the direction if the field already exists.
  const addSort = (field, dir) => {
    setFilters(prev => {
      const chain = Array.isArray(prev.sort) ? [...prev.sort] : [];
      // If only the implicit default Name↑ sort is active, replace it so the first chosen column becomes primary.
      if (chain.length === 1 && chain[0].field === 'name' && chain[0].dir === 'asc') {
        return { ...prev, sort: [{ field, dir }] };
      }
      const idx = chain.findIndex(s => s.field === field);
      if (idx >= 0) chain[idx] = { field, dir };
      else chain.push({ field, dir });
      return { ...prev, sort: chain };
    });
    setAddSortOpen(false);
  };

  // Click a chip to flip its sort direction.
  const toggleChipDir = (field) => setFilters(prev => ({
    ...prev,
    sort: (prev.sort || []).map(s => s.field === field ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s),
  }));

  const SortIcon = ({ field }) => {
    const chain = filters.sort || [];
    const idx = chain.findIndex(s => s.field === field);
    if (idx < 0) return <span className="text-muted-foreground/25 ml-0.5">↕</span>;
    return (
      <span className="text-blue-400 ml-0.5 whitespace-nowrap">
        {chain.length > 1 && <sup className="text-[8px] mr-0.5">{idx + 1}</sup>}
        {chain[idx].dir === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const Th = ({ field, children, className = '' }) => (
    <th className={`py-2 px-3 cursor-pointer hover:text-foreground select-none ${className}`} onClick={(e) => toggleSort(field, e.shiftKey)} title="Click to sort · Shift+Click to add a sort level" data-testid={`sort-${field}`}>
      {children} <SortIcon field={field} />
    </th>
  );

  const bandColor = (band, score) => {
    const b = (band || '').toLowerCase();
    if (b === 'high' || (score != null && score >= 80)) return 'text-emerald-400';
    if (b === 'medium' || (score != null && score >= 55)) return 'text-amber-400';
    return 'text-muted-foreground';
  };

  const aiMode = aiRunId !== null || aiResults !== null;
  const aiLive = aiResults !== null; // run in progress → progressive rows from polling
  // Exclusion keywords filter the live AI hits client-side; once done, ALL filters
  // apply server-side via the ai_run_id intersection (Talu, 19.08.2026)
  const aiExcluded = (filters.exclude_keywords || []).map(k => String(k).toLowerCase().trim()).filter(Boolean);
  const aiFiltered = aiLive
    ? (aiExcluded.length
      ? aiResults.filter(r => {
          const text = `${r.name || ''} ${r.random_project_name || ''} ${r.description || ''} ${r.products || ''} ${r.subsector || ''} ${r.sector || ''}`.toLowerCase();
          return !aiExcluded.some(k => text.includes(k));
        })
      : aiResults)
    : null;
  const aiHidden = aiLive ? aiResults.length - aiFiltered.length : 0;
  const displayResults = aiLive ? aiFiltered : results;
  const matchColor = (s) => (s >= 70 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40'
    : s >= 40 ? 'text-amber-400 bg-amber-500/10 border-amber-500/40'
    : 'text-muted-foreground/70 bg-secondary border-border');

  return (
    <div className="space-y-4" data-testid="explorer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight">Deal Explorer</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {aiMode
              ? (aiLive
                ? `${displayResults.length} KI-Treffer${aiHidden > 0 ? ` · ${aiHidden} durch Ausschluss-Keywords ausgeblendet` : ''}${['running', 'interpreting'].includes(aiStatus) ? ' (läuft…)' : ''}`
                : `${total.toLocaleString()} KI-Treffer — mit Filtern eingegrenzt, sortiert nach Match-Score`)
              : loading ? 'Searching...' : `${total.toLocaleString()} deals found`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
          <button onClick={startBulk} disabled={bulkRun?.status === 'running'}
            data-testid="bulk-quick-score-btn"
            title="Bulk-KI-Nachschärfung: Claude bewertet die obersten 100 Treffer der aktuellen Ansicht (Filter + Sortierung + KI-Suche) automatisch nach — bereits KI-bewertete Deals werden übersprungen"
            className="text-xs h-8 px-2.5 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
            {bulkRun?.status === 'running'
              ? <><Loader2 className="w-3 h-3 animate-spin" /> KI schärft nach… {bulkRun.processed || 0}/{bulkRun.total || 0}</>
              : <><Sparkles className="w-3 h-3" /> Top 100 KI-Nachschärfung</>}
          </button>
          )}
          {isAdmin && (
            <button onClick={startHeuristics} disabled={heurStatus?.run?.status === 'running'}
              data-testid="run-heuristics-btn"
              title="Succession Score & Infra Fit heuristisch (ohne KI) für alle Firmen berechnen — nur fehlende Werte werden ergänzt"
              className="text-xs h-8 px-2.5 rounded-sm border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
              {heurStatus?.run?.status === 'running'
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Heuristik läuft… {(heurStatus.run.processed || 0).toLocaleString()}/{(heurStatus.run.total || 0).toLocaleString()}</>
                : <><FlaskConical className="w-3 h-3" /> Heuristik-Scores</>}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setDupOpen(true)} data-testid="duplicate-review-btn"
              title="Doppelte Deals (gleicher Firmenname) prüfen und bereinigte Kopien löschen"
              className="text-xs h-8 px-2.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5">
              <Copy className="w-3 h-3" /> Duplikate
            </button>
          )}
          <DuplicateReviewDialog open={dupOpen} onOpenChange={setDupOpen} onDeleted={() => search(1)} />
          <button onClick={() => updateFilter('ac_flagged', !filters.ac_flagged)} data-testid="ac-flag-filter-toggle"
            title='Show only deals earmarked with "!" for the Advisory Committee'
            className={`text-xs h-8 px-2.5 rounded-sm border font-bold transition-colors ${filters.ac_flagged ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            ! AC deals
          </button>
          <Select value={filters.ac_feedback || 'none'} onValueChange={(v) => updateFilter('ac_feedback', v === 'none' ? '' : v)}>
            <SelectTrigger data-testid="ac-feedback-filter"
              className={`h-8 w-auto gap-1.5 text-xs rounded-sm border ${filters.ac_feedback ? 'bg-purple-500/15 border-purple-500/40 text-purple-300' : 'border-border text-muted-foreground bg-transparent'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="none" className="text-xs">AC feedback: All</SelectItem>
              <SelectItem value="any" className="text-xs">Has AC feedback</SelectItem>
              <SelectItem value="interested" className="text-xs">AC: Interested</SelectItem>
              <SelectItem value="questions" className="text-xs">AC: Questions</SelectItem>
              <SelectItem value="decline" className="text-xs">AC: Not interested</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={() => updateFilter('exclude_claimed', !filters.exclude_claimed)} data-testid="hide-claimed-toggle"
            className={`text-xs h-8 px-2.5 rounded-sm border transition-colors ${filters.exclude_claimed ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {filters.exclude_claimed ? 'Showing unclaimed' : 'Hide claimed'}
          </button>
          <Button variant="ghost" onClick={resetFilters} className="text-xs text-muted-foreground h-8 px-2" data-testid="reset-filters-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
          <Button variant="ghost" onClick={() => setFiltersOpen(!filtersOpen)} className="text-xs text-muted-foreground h-8 px-2">
            <ChevronDown className={`w-3 h-3 mr-1 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} /> Filters
          </Button>
        </div>
      </div>

      {/* KI-Suche — semantische Suche per Geschäftsbeschreibung */}
      <AiDealSearch onResults={onAiResults} getFilters={getAiFilters} />

      {/* Search + Filters */}
      {filtersOpen && (
        <DealFilterPanel filters={filters} updateFilter={updateFilter} setFilters={setFilters}
          sectorConfig={sectorConfig} ownerOptions={ownerOptions} onSearch={() => search(1)} />
      )}

      {/* Active sort chain */}
      {!aiMode && (
      <div className="flex items-center flex-wrap gap-1.5 text-xs text-muted-foreground" data-testid="sort-chain">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Sort:</span>
        {(filters.sort || []).map((s, i) => (
          <span key={s.field} className="inline-flex items-center gap-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-sm px-1.5 py-0.5 font-mono" data-testid={`sort-chip-${s.field}`}>
            {(filters.sort || []).length > 1 && <span className="text-[9px] opacity-60">{i + 1}</span>}
            <button onClick={() => toggleChipDir(s.field)} className="hover:text-blue-200" title="Richtung umschalten" data-testid={`sort-chip-dir-${s.field}`}>
              {SORT_LABELS[s.field] || s.field} {s.dir === 'asc' ? '↑' : '↓'}
            </button>
            <button onClick={() => removeSort(s.field)} className="hover:text-blue-200" data-testid={`sort-chip-remove-${s.field}`}><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
        {/* Explicit add-sort control (no Shift+Click needed; mobile-friendly) */}
        <Popover open={addSortOpen} onOpenChange={setAddSortOpen}>
          <PopoverTrigger asChild>
            <button data-testid="add-sort-btn"
              className="inline-flex items-center gap-1 border border-dashed border-blue-500/40 text-blue-400 hover:bg-blue-500/10 rounded-sm px-1.5 py-0.5 text-[11px] font-mono transition-colors">
              <Plus className="w-3 h-3" /> Sortierung
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2.5 bg-card border-border rounded-sm space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Sortierebene hinzufügen</p>
            <Select value={sortAddField} onValueChange={setSortAddField}>
              <SelectTrigger data-testid="add-sort-field" className="bg-secondary border-border rounded-sm h-8 text-xs"><SelectValue placeholder="Spalte" /></SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                {Object.entries(SORT_LABELS).map(([f, l]) => <SelectItem key={f} value={f} className="text-xs">{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortAddDir} onValueChange={setSortAddDir}>
              <SelectTrigger data-testid="add-sort-dir" className="bg-secondary border-border rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="desc" className="text-xs">Absteigend ↓</SelectItem>
                <SelectItem value="asc" className="text-xs">Aufsteigend ↑</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => addSort(sortAddField, sortAddDir)} data-testid="add-sort-apply"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm h-8 text-xs">Hinzufügen</Button>
          </PopoverContent>
        </Popover>
        <span className="text-[10px] text-muted-foreground/40 ml-1 hidden sm:inline">oder Shift+Klick auf einen Spaltenkopf</span>
      </div>
      )}

      {/* Results Table */}
      <div className="bg-card border border-border rounded-sm" data-testid="explorer-results">
        <table className="w-full text-left table-fixed">
          <thead>
            <tr className="border-b border-border text-[10px] text-muted-foreground uppercase tracking-wider">
              {aiMode && <th className="py-2 px-3 w-[7%]" data-testid="ai-match-header">Match</th>}
              <Th field="name" className="w-[17%]">Name</Th>
              <Th field="sector" className="w-[13%]">Sector</Th>
              <Th field="thesis_score" className="text-right w-[6%] hidden md:table-cell">Thesis</Th>
              <Th field="revenue" className="text-right w-[8%]">Rev €m</Th>
              <Th field="employees" className="text-right w-[7%]">Empl</Th>
              <Th field="rev_per_employee" className="text-right w-[8%]">€k/Empl</Th>
              <Th field="founded" className="w-[7%] hidden md:table-cell">Founded</Th>
              <Th field="succession_score" className="w-[11%] hidden lg:table-cell">Succession</Th>
              <Th field="infra" className="w-[7%] hidden lg:table-cell">Infra</Th>
              <th className="py-2 px-3 w-[10%] hidden sm:table-cell">Claimed by</th>
              <th className="py-2 px-3 w-[5%]"></th>
            </tr>
          </thead>
          <tbody>
            {displayResults.map(r => (
              <React.Fragment key={r.id}>
              <tr onClick={() => openDeal(r.id)} data-testid={`explorer-row-${r.id}`}
                className="border-b border-border/40 even:bg-secondary/40 row-hover-glow cursor-pointer text-sm">
                {aiMode && (
                  <td className="py-2.5 px-3 align-top" data-testid={`ai-score-${r.id}`}>
                    <span className={`text-[11px] font-mono font-bold rounded px-1.5 py-0.5 border ${matchColor(r.ai_score || 0)}`}>
                      {r.ai_score != null ? r.ai_score : '—'}
                    </span>
                  </td>
                )}
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-1.5">
                    {viewedSet.has(r.id) && (
                      <span title="You've already viewed this deal" className="shrink-0 inline-flex" data-testid={`viewed-icon-${r.id}`}>
                        <Eye className="w-3.5 h-3.5 text-blue-400/70" aria-label="Viewed" />
                      </span>
                    )}
                    {r.ac_flagged && (
                      <span title="Earmarked for AC meeting" data-testid={`ac-flag-icon-${r.id}`}
                        className="shrink-0 w-4 h-4 rounded-full bg-amber-500/20 border border-amber-500/60 text-amber-400 text-[10px] font-bold leading-none flex items-center justify-center">
                        !
                      </span>
                    )}
                    <span className="text-foreground font-medium truncate">{r.name}</span>
                    {r.website && (
                      <a href={websiteUrl(r.website)} target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()} title={`Website öffnen: ${r.website}`}
                        data-testid={`explorer-website-${r.id}`}
                        className="shrink-0 text-muted-foreground/50 hover:text-sky-400 transition-colors">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {r.pipeline_stage === 'passed' && !r.benched && (
                      <span data-testid={`passed-badge-${r.id}`}
                        className="shrink-0 text-[9px] font-mono font-bold text-red-400/80 bg-red-400/10 border border-red-400/30 rounded px-1 py-0.5">
                        PASSED
                      </span>
                    )}
                    {r.benched && (
                      <span title="Passed, but benched — still searchable" data-testid={`benched-badge-${r.id}`}
                        className="shrink-0 text-[9px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded px-1 py-0.5">
                        BENCH
                      </span>
                    )}
                  </div>
                  {r.random_project_name && <CodeNameTag name={r.random_project_name} block className="text-[10px] text-blue-400/70 font-mono truncate" testId={`explorer-code-name-${r.id}`} />}
                  {aiMode && r.ai_reason && (
                    <p className="text-[10px] text-sky-300/80 leading-snug mt-0.5" data-testid={`ai-reason-${r.id}`}
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      title={r.ai_reason}>
                      {r.ai_reason}
                    </p>
                  )}
                </td>
                <td className="py-2.5 px-3">
                  <div className="text-xs text-foreground/80 truncate">
                    {r.sector || <span className="text-muted-foreground/40">—</span>}
                    {r.olta_sector_guess && (
                      <span title="OLTA-Sektor (heuristisch zugeordnet)" data-testid={`sector-guess-${r.id}`}
                        className="ml-1.5 text-[9px] font-mono text-violet-300 bg-violet-500/10 border border-violet-500/40 rounded px-1 py-0.5 whitespace-nowrap">→ {r.olta_sector_guess}</span>
                    )}
                  </div>
                  {r.description ? (
                    <div onClick={(e) => toggleDesc(e, r.id)} role="button"
                      className="text-[10px] text-muted-foreground leading-snug cursor-pointer hover:text-foreground/70"
                      title={expandedDesc.has(r.id) ? 'Beschreibung einklappen' : 'Klicken für die volle Beschreibung'}
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      data-testid={`explorer-desc-${r.id}`}>
                      {r.description}
                    </div>
                  ) : null}
                </td>
                <td className="py-2.5 px-3 text-xs text-right hidden md:table-cell" data-testid={`thesis-cell-${r.id}`}>
                  {r.thesis_score != null ? (
                    <span title={r.thesis_reason || ''} className={`text-[11px] font-mono font-bold rounded px-1.5 py-0.5 border ${matchColor(r.thesis_score)}`}>{r.thesis_score}</span>
                  ) : <span className="text-muted-foreground/30">—</span>}
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground/70 font-mono text-right">{fmtRevenue1(r.revenue) || '—'}</td>
                <td className="py-2.5 px-3 text-xs text-foreground/70 font-mono text-right">{fmtInt(r.employees) || '—'}</td>
                <td className="py-2.5 px-3 text-xs text-foreground/70 font-mono text-right" data-testid={`rpe-cell-${r.id}`}>
                  {r.rev_per_employee != null ? fmtInt(r.rev_per_employee) : <span className="text-muted-foreground/40">n.a.</span>}
                </td>
                <td className="py-2.5 px-3 text-xs text-muted-foreground font-mono hidden md:table-cell">{r.founded || '—'}</td>
                <td className="py-2.5 px-3 text-xs hidden lg:table-cell truncate" data-testid={`succession-cell-${r.id}`}>
                  {r.succession_score != null ? (
                    <span className={`font-mono font-medium ${bandColor(r.succession_band, r.succession_score)}`} title={r.succession_rationale || ''}>
                      {r.succession_score}<span className="text-muted-foreground/40">/100</span>
                      {r.succession_band && <span className="ml-1 text-[10px]">{r.succession_band}</span>}
                    </span>
                  ) : r.succession_case ? (
                    <span className={`${r.succession_case === 'yes' ? 'text-emerald-400' : r.succession_case === 'maybe' ? 'text-amber-400' : 'text-muted-foreground'}`}>{r.succession_case}</span>
                  ) : <span className="text-muted-foreground/30">—</span>}
                  {r.heuristic_scored && (
                    <span title={`Heuristische Bewertung (ohne KI)${r.infra_rationale ? ` — ${r.infra_rationale}` : ''}`} data-testid={`heuristic-badge-${r.id}`}
                      className="ml-1.5 text-[9px] font-mono font-bold text-violet-300 bg-violet-500/10 border border-violet-500/40 rounded px-1 py-0.5">H</span>
                  )}
                  {r.ai_quick_scored && (
                    <span title="Per KI-Schnellanalyse bewertet" data-testid={`ai-badge-${r.id}`}
                      className="ml-1.5 text-[9px] font-mono font-bold text-sky-300 bg-sky-500/10 border border-sky-500/40 rounded px-1 py-0.5">KI</span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-xs hidden lg:table-cell" data-testid={`infra-cell-${r.id}`}>
                  {r.infra_adjacent ? (
                    <span title={r.infra_rationale || ''}
                      className={`text-[10px] font-mono font-bold rounded px-1.5 py-0.5 border ${
                        r.infra_adjacent === 'fit' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40'
                        : r.infra_adjacent === 'fringe' ? 'text-amber-400 bg-amber-500/10 border-amber-500/40'
                        : 'text-muted-foreground/60 bg-secondary border-border'}`}>
                      {r.infra_adjacent === 'no_fit' ? 'NO FIT' : r.infra_adjacent.toUpperCase()}
                    </span>
                  ) : <span className="text-muted-foreground/30">—</span>}
                </td>
                <td className="py-2.5 px-3 text-xs hidden sm:table-cell" data-testid={`claimed-cell-${r.id}`}>
                  {r.owner_id ? <span className="text-foreground/70 truncate block">{r.owner_name}</span> : <span className="text-muted-foreground/30">—</span>}
                </td>
                <td className="py-2.5 px-3 text-right whitespace-nowrap">
                  {r.pipeline_stage !== 'passed' && (
                    <button onClick={(e) => openPassDialog(e, r)}
                      data-testid={`explorer-pass-btn-${r.id}`}
                      title="Pass — direkt von hier aussortieren"
                      className="text-muted-foreground/40 hover:text-red-400 transition-colors mr-1 align-middle">
                      <Ban className="w-3.5 h-3.5 inline" />
                    </button>
                  )}
                  <button onClick={(e) => quickScore(e, r)} disabled={quickScoring === r.id}
                    data-testid={`quick-score-btn-${r.id}`}
                    title="KI-Schnellanalyse: Claude bewertet Succession & Infra Fit aus dem Profil (überschreibt Heuristik)"
                    className="text-muted-foreground/40 hover:text-sky-400 transition-colors mr-1 align-middle">
                    {quickScoring === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : <Sparkles className="w-3.5 h-3.5 inline" />}
                  </button>
                  <ChevronRight className="w-3 h-3 text-muted-foreground/20 inline" />
                </td>
              </tr>
              {expandedDesc.has(r.id) && r.description && (
                <tr className="border-b border-border/40 bg-secondary/30" data-testid={`explorer-desc-row-${r.id}`}>
                  <td colSpan={aiMode ? 12 : 11} className="px-4 py-3">
                    <div className="flex items-start gap-3 flex-wrap">
                      <p className="text-xs text-foreground/85 leading-relaxed flex-1 min-w-[300px] max-w-5xl">{r.description}</p>
                      <div className="flex items-center gap-3 shrink-0">
                        {r.website && (
                          <a href={websiteUrl(r.website)} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} data-testid={`explorer-desc-website-${r.id}`}
                            className="text-[11px] text-sky-400 hover:text-sky-300 flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> {r.website.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                        <button onClick={(e) => toggleDesc(e, r.id)} data-testid={`explorer-desc-collapse-${r.id}`}
                          className="text-[11px] text-muted-foreground hover:text-foreground">Einklappen</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {displayResults.length === 0 && !loading && !(aiMode && ['running', 'interpreting'].includes(aiStatus)) && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {aiMode ? 'Keine KI-Treffer innerhalb der aktuellen Filter — Filter lockern oder Beschreibung/Begriffe anpassen' : 'No deals match your filters'}
          </p>
        )}

        {aiMode && ['running', 'interpreting'].includes(aiStatus) && displayResults.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-sky-300" data-testid="ai-search-table-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            {aiStatus === 'interpreting' ? 'KI interpretiert deine Beschreibung…' : 'KI bewertet Kandidaten…'}
          </div>
        )}

        {!aiMode && loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Load More */}
        {!aiMode && results.length < total && !loading && (
          <div className="text-center py-3 border-t border-border">
            <Button variant="ghost" onClick={() => search(page + 1)} className="text-xs text-muted-foreground h-8">
              Load More — showing {results.length} of {total.toLocaleString()}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={!!passTarget} onOpenChange={(o) => { if (!o) setPassTarget(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="explorer-pass-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Pass — {passTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {passTarget && !passTarget.owner_id && ['orbis_total', 'succession_possible', 'ai_reviewed'].includes(passTarget.pipeline_stage)
              ? 'Unclaimter TAM-Eintrag — wird direkt final aussortiert (kein Vier-Augen nötig).'
              : 'Geclaimter/fortgeschrittener Deal — es wird ein Pass-Vorschlag erstellt (Vier-Augen-Bestätigung).'}
          </p>
          <PassReasonPicker selected={passTags} onToggle={(id) =>
            setPassTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id])} />
          <textarea value={passText} onChange={e => setPassText(e.target.value)} rows={2} maxLength={300}
            placeholder="Optionaler Zusatz zum Grund…" data-testid="explorer-pass-reason-input"
            className="w-full bg-secondary border border-border rounded-sm p-2 text-xs text-foreground resize-none focus:outline-none focus:border-red-500/50" />
          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
            <Checkbox checked={passBench} onCheckedChange={v => setPassBench(!!v)} data-testid="explorer-pass-bench" />
            Benchen — bleibt im Explorer auffindbar (Passed, Benched)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPassTarget(null)} className="text-xs h-8 px-3">Abbrechen</Button>
            <Button disabled={passing || (!passTags.length && !passText.trim())} data-testid="explorer-pass-confirm-btn"
              onClick={submitPass}
              className="bg-red-700 hover:bg-red-600 text-white rounded-sm font-medium text-xs h-8 px-3">
              {passing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pass'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
